import {
  NO_BACK_SCREENS, POST_LOGIN_SCREENS, LOGIN_ENTRY_SCREENS,
} from 'lib/loginFormConstants';

/**
 * Checks whether the given screen is an entry point screen
 * (e.g. Start, Login, Create Account).
 *
 * @param {string|null} screen - The screen to check.
 * @returns {boolean} True if the screen is an entry screen.
 */
export const isLoginEntry = (screen) => LOGIN_ENTRY_SCREENS.includes(screen);

/**
 * Checks whether the given screen is one where the back button should never appear.
 *
 * @param {string} screen - The screen to check.
 * @returns {boolean} True if back should never be shown.
 */
export const isNoBackScreen = (screen) => NO_BACK_SCREENS.includes(screen);

/**
 * Checks whether the given screen is a post-login screen.
 *
 * @param {string} screen - The screen to check.
 * @returns {boolean} True if the screen is a post-login screen.
 */
export const isPostLogin = (screen) => POST_LOGIN_SCREENS.includes(screen);

/**
 * Pushes the previous screen into the history stack (non-mutating).
 *
 * @param {string[]} history - The current history stack.
 * @param {string|null} prev - The previous screen.
 * @param {string} next - The next (current) screen.
 * @returns {string[]} A new history stack with `prev` appended, or unchanged if guard conditions apply.
 */
export const pushPrev = (history, prev, next) => {
  if (!prev || prev === next) return history ?? [];
  return [...(Array.isArray(history) ? history : []), prev];
};

/**
 * Returns the last screen in history that can be navigated back to.
 *
 * Applies the post-login guard: if current is post-login and previous is a login entry screen,
 * return null instead.
 *
 * @param {string[]} history - The current history stack.
 * @param {string} current - The current screen.
 * @returns {string|null} The back target screen, or null if none.
 */
export const topTarget = (history, current) => {
  if (!history?.length) return null;
  const prev = history[history.length - 1];
  if (isPostLogin(current) && isLoginEntry(prev)) return null;
  return prev;
};

/**
 * Removes the last screen from the history stack (non-mutating).
 *
 * @param {string[]} history - The current history stack.
 * @returns {string[]} A new history stack without the last screen.
 */
export const pop = (history) => (!history?.length ? [] : history.slice(0, -1));
